import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";

export interface BrandStats {
  customers?: string;
  rating?: string;
  reviews?: string;
  loans_funded?: string;
  response_time?: string;
}

const defaultStats: BrandStats = {
  customers: "500,000+",
  rating: "4.9/5",
  reviews: "10k+",
  loans_funded: "$5B+",
  response_time: "24hrs",
};

export interface BrandConfig {
  entityId: string | null;
  brandName: string;
  brandDomain: string;
  logoUrl: string | null;
  faviconUrl: string | null;
  brandColorPrimary: string | null;
  brandTagline: string | null;
  heroLenders: string[];
  ogImageUrl: string | null;
  brandStats: BrandStats;
  urlCode: string;
  isLoaded: boolean;
}

const defaults: BrandConfig = {
  entityId: null,
  brandName: "Top Loan Offers",
  brandDomain: "toploanoffers.com",
  logoUrl: null,
  faviconUrl: null,
  brandColorPrimary: null,
  brandTagline: null,
  heroLenders: ["Lender1", "Lender2", "Lender3", "Lender4"],
  ogImageUrl: null,
  brandStats: defaultStats,
  urlCode: "default",
  isLoaded: false,
};

export function useBrandConfig(): BrandConfig {
  const [config, setConfig] = useState<BrandConfig>(defaults);

  useEffect(() => {
    const fetchBrand = async () => {
      const hostname = window.location.hostname;

      const { data: entities } = await supabase.rpc("get_brand_config" as any);

      if (!entities || (entities as any[]).length === 0) {
        setConfig({ ...defaults, isLoaded: true });
        return;
      }

      const list = entities as any[];
      const domainMatch = list.find((e: any) => e.brand_domain === hostname);
      const entity = domainMatch || list.find((e: any) => e.is_default) || list[0];

      // Merge brand_stats from DB with defaults
      const dbStats = entity.brand_stats as BrandStats | null;
      const mergedStats: BrandStats = { ...defaultStats, ...(dbStats || {}) };

      setConfig({
        entityId: entity.id || null,
        brandName: entity.brand_name || entity.name || defaults.brandName,
        brandDomain: entity.brand_domain || defaults.brandDomain,
        logoUrl: entity.logo_url || null,
        faviconUrl: entity.favicon_url || null,
        brandColorPrimary: entity.brand_color_primary || null,
        brandTagline: entity.brand_tagline || null,
        heroLenders: entity.hero_lenders?.length ? entity.hero_lenders : defaults.heroLenders,
        ogImageUrl: entity.og_image_url || null,
        brandStats: mergedStats,
        urlCode: entity.url_code || "default",
        isLoaded: true,
      });
    };

    fetchBrand();
  }, []);

  return config;
}
